import { isCLTCrossSectionKind, isTimberOrSteelCrossSectionKind } from '@domainUtils'
import { calculateCumulativeThickness } from 'src/components/manager/assemblies/utils'
import { remapMaterialIdentifier } from 'src/components/pages/Editor/components/SingleElementCSForm/components/FormFields'
import { AssemblyKind } from 'src/constants/assemblies'
import {
  getCltLayer,
  getSlabBeamCrossSectionFromAssembly,
  getSlabBeamStepSizeFromAssembly,
} from 'src/utils/assemblies'

/**
 * Label lines exactly as the plan PDF export writes them (`makeCrossSectionLabel`
 * and `makeSlabAssemblyLabel` in the backend's `functions/export/plans/utils.py`).
 * The floorplan labels size their boxes from this text, so it has to match the
 * export character for character - the Results pages' `makeCrossSectionLabel`
 * deliberately differs (no unit, other step size notation).
 */

/** `f"{cm:.1f}".rstrip("0").rstrip(".")` - at most one decimal, no trailing zero */
const centimeterFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  useGrouping: false,
})

const formatCentimeters = (meters: number): string => centimeterFormatter.format(meters * 100)

/** `f"{cm:.2f}".rstrip("0").rstrip(".")` - cross section b/h: up to two decimals, no trailing zeros */
const crossSectionCentimeterFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 2,
  useGrouping: false,
})

const formatCrossSectionCentimeters = (meters: number): string =>
  crossSectionCentimeterFormatter.format(meters * 100)

/** e.g. `12/24cm C24`, `HEA 200 S235`, or a CLT identifier */
export const makePlanCrossSectionLabel = (
  crossSection: CrossSection | CLTCrossSection,
): string | undefined => {
  if ('material' in crossSection && isTimberOrSteelCrossSectionKind(crossSection)) {
    const { shape } = crossSection
    if (shape.kind_literal === 'RectangularCS') {
      const width = formatCrossSectionCentimeters(shape.width)
      const height = formatCrossSectionCentimeters(shape.height)
      return `${width}/${height}cm ${remapMaterialIdentifier(crossSection.material.identifier)}`
    }
    if (shape.kind_literal === 'SteelCS') {
      return `${shape.profile} ${crossSection.material.identifier}`
    }
    return undefined
  }
  if (isCLTCrossSectionKind(crossSection)) return crossSection.identifier
  return undefined
}

/**
 * The slab position labels' assembly line: rip cross section and step size
 * (`6/24cm C24, a=62.5`) for timber frame slabs, `BSP<thickness>` for CLT slabs.
 * Throws (via the assembly utils) on assemblies without the expected layer.
 */
export const makePlanSlabAssemblyLabel = (assembly: Assembly): string | undefined => {
  switch (assembly.kind) {
    case AssemblyKind.TimberFrameRoof:
    case AssemblyKind.TimberFrameCeiling: {
      const crossSection = makePlanCrossSectionLabel(getSlabBeamCrossSectionFromAssembly(assembly))
      return `${crossSection}, a=${formatCentimeters(getSlabBeamStepSizeFromAssembly(assembly))}`
    }
    case AssemblyKind.CLTRoof:
    case AssemblyKind.CLTCeiling:
      // `thicknessOfCLTOnly` on the backend: the CLT layers without any build-up
      return `BSP${formatCentimeters(
        calculateCumulativeThickness(getCltLayer(assembly).clt.layers),
      )}`
    default:
      return undefined
  }
}

/**
 * Slab position label lines: the label, plus the assembly line when cross
 * sections are shown. The assembly utils throw on assemblies without the
 * expected layers, which just means there is no assembly line to show.
 */
export const makePlanSlabLabelLines = (
  label: string,
  assembly: Assembly | undefined,
  showCrossSections: boolean,
): string[] => {
  if (!showCrossSections || !assembly) return [label]
  try {
    const assemblyLine = makePlanSlabAssemblyLabel(assembly)
    return assemblyLine ? [label, assemblyLine] : [label]
  } catch {
    return [label]
  }
}
